import React, { useState, useEffect, useMemo, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLookups } from '../../../../global_configs/hooks/useLookups.ts'
import { FormSection } from '../../../../../core/components/FormSection.tsx'
import { InputManager } from '../../../../../core/components/InputManager/index.ts'
import { updateCompanyRecord } from '../../../../../state_manager/state_update_actions/companies/index.ts'
import { AccountRecordFields } from '../../../../../state_manager/data_models/account_record.ts'
import { getGeneralFields, type SourcingContactsByRole } from '../../../utils/fieldConfigs.ts'
import { AccountRecord } from '../../../../../state_manager/data_models/account_record.ts'
import { UserEntryFields } from '../../../../../state_manager/data_models/user_entry.ts'
import { CompanyTeamEntryFields } from '../../../../../state_manager/data_models/company_team_entry.ts'
import { UNKNOWN_CONTACT } from '../../../../../state_manager/constants/strings.ts'
import {
  RELATIONSHIP_CATEGORY_INTRODUCED_BY,
  RELATIONSHIP_CATEGORY_SOURCED_BY
} from '../../../../../state_manager/globals.ts'
import { fetchCompanyById } from '../../../services/companiesService.ts'
import { getAccessToken } from '../../../../../core/auth/authService'
import { store } from '../../../../../state_manager/store.ts'

export function GeneralSection({
  company,
  formData,
  handleFieldChange,
  onPatchSuccess,
  showTitle = true,
  cardProps = {},
  isReadOnly = false,
  recordId = null
}: {
  company?: AccountRecord | null
  formData?: Record<string, any>
  handleFieldChange?: (field: string, value: any) => void
  onPatchSuccess?: (patchRecordId: string, updates: Record<string, any>) => void
  showTitle?: boolean
  cardProps?: Record<string, any>
  isReadOnly?: boolean
  recordId?: string | null
}) {
  const dispatch = useDispatch()
  const { detailsByCategory } = useLookups()
  const companyLookupId =
    detailsByCategory?.['Global Configs']?.find((option: any) => option.label === 'internal_company_id')?.value || null

  const data = company || formData || {}

  const companyId =
    recordId || company?.[AccountRecordFields.accounts__id] || data[AccountRecordFields.accounts__id] || data.id

  const [selectedContacts, setSelectedContacts] = useState<{
    introduced_by: any[]
    sourced_by: any[]
  }>({
    introduced_by: [],
    sourced_by: []
  })

  const [previousContacts, setPreviousContacts] = useState<{
    introduced_by: any[]
    sourced_by: any[]
  }>({
    introduced_by: [],
    sourced_by: []
  })

  const hasInitializedRef = useRef(false)

  useEffect(() => {
    const otherTeam = data[AccountRecordFields.prog__other_team] || []
    const introducedByDetail = data[AccountRecordFields.contact_accounts__introduced_by] || data.introduced_by_detail
    const sourcedByDetails = data[AccountRecordFields.contact_accounts__sourced_by] || data.sourced_by_details

    const contactsByRole: SourcingContactsByRole = {
      introduced_by: [],
      sourced_by: []
    }

    if (otherTeam && Array.isArray(otherTeam) && otherTeam.length > 0) {
      const introducedByFromOtherTeam = otherTeam.filter(
        (contact: any) =>
          contact[CompanyTeamEntryFields.contact_accounts__relationship_category_id] ===
          RELATIONSHIP_CATEGORY_INTRODUCED_BY
      )
      const sourcedByFromOtherTeam = otherTeam.filter(
        (contact: any) =>
          contact[CompanyTeamEntryFields.contact_accounts__relationship_category_id] ===
          RELATIONSHIP_CATEGORY_SOURCED_BY
      )

      contactsByRole.introduced_by = introducedByFromOtherTeam.map((contact: any) => {
        const contactAccountsId =
          contact[CompanyTeamEntryFields.contact_accounts__id] ||
          contact[UserEntryFields.contact_accounts__id] ||
          contact.contact_accounts__id ||
          null
        return {
          value:
            contact[UserEntryFields.contacts__id] || contact[CompanyTeamEntryFields.contacts__id] || contact.id || '',
          label:
            contact[UserEntryFields.prog__display_name] ||
            contact[CompanyTeamEntryFields.prog__display_name] ||
            `${contact[UserEntryFields.contacts__first_name] || contact[CompanyTeamEntryFields.contacts__first_name] || ''} ${contact[UserEntryFields.contacts__last_name] || contact[CompanyTeamEntryFields.contacts__last_name] || ''}`.trim() ||
            contact[UserEntryFields.contacts__email] ||
            contact[CompanyTeamEntryFields.contacts__email] ||
            UNKNOWN_CONTACT,
          email: contact[UserEntryFields.contacts__email] || contact[CompanyTeamEntryFields.contacts__email] || '',
          contact_accounts_id: contactAccountsId,
          data: contact
        }
      })

      contactsByRole.sourced_by = sourcedByFromOtherTeam.map((contact: any) => {
        const contactAccountsId =
          contact[CompanyTeamEntryFields.contact_accounts__id] ||
          contact[UserEntryFields.contact_accounts__id] ||
          contact.contact_accounts__id ||
          null
        return {
          value:
            contact[UserEntryFields.contacts__id] || contact[CompanyTeamEntryFields.contacts__id] || contact.id || '',
          label:
            contact[UserEntryFields.prog__display_name] ||
            contact[CompanyTeamEntryFields.prog__display_name] ||
            `${contact[UserEntryFields.contacts__first_name] || contact[CompanyTeamEntryFields.contacts__first_name] || ''} ${contact[UserEntryFields.contacts__last_name] || contact[CompanyTeamEntryFields.contacts__last_name] || ''}`.trim() ||
            contact[UserEntryFields.contacts__email] ||
            contact[CompanyTeamEntryFields.contacts__email] ||
            UNKNOWN_CONTACT,
          email: contact[UserEntryFields.contacts__email] || contact[CompanyTeamEntryFields.contacts__email] || '',
          contact_accounts_id: contactAccountsId,
          data: contact
        }
      })
    } else {
      if (introducedByDetail && Array.isArray(introducedByDetail)) {
        contactsByRole.introduced_by = introducedByDetail.map((contact: any) => {
          const contactAccountsId =
            contact[CompanyTeamEntryFields.contact_accounts__id] ||
            contact[UserEntryFields.contact_accounts__id] ||
            contact.contact_accounts__id ||
            null
          return {
            value: contact[UserEntryFields.contacts__id] || contact.id || '',
            label:
              contact[UserEntryFields.prog__display_name] ||
              `${contact[UserEntryFields.contacts__first_name] || ''} ${contact[UserEntryFields.contacts__last_name] || ''}`.trim() ||
              contact[UserEntryFields.contacts__email] ||
              UNKNOWN_CONTACT,
            email: contact[UserEntryFields.contacts__email] || '',
            contact_accounts_id: contactAccountsId,
            data: contact
          }
        })
      }

      if (sourcedByDetails && Array.isArray(sourcedByDetails)) {
        contactsByRole.sourced_by = sourcedByDetails.map((contact: any) => {
          const contactAccountsId =
            contact[CompanyTeamEntryFields.contact_accounts__id] ||
            contact[UserEntryFields.contact_accounts__id] ||
            contact.contact_accounts__id ||
            null
          return {
            value: contact[UserEntryFields.contacts__id] || contact.id || '',
            label:
              contact[UserEntryFields.prog__display_name] ||
              `${contact[UserEntryFields.contacts__first_name] || ''} ${contact[UserEntryFields.contacts__last_name] || ''}`.trim() ||
              contact[UserEntryFields.contacts__email] ||
              UNKNOWN_CONTACT,
            email: contact[UserEntryFields.contacts__email] || '',
            contact_accounts_id: contactAccountsId,
            data: contact
          }
        })
      }
    }

    const hasSelectedData = contactsByRole.introduced_by?.length > 0 || contactsByRole.sourced_by?.length > 0
    const hasPreviousData = previousContacts.introduced_by?.length > 0 || previousContacts.sourced_by?.length > 0
    const selectedIntroducedByIds = contactsByRole.introduced_by?.map((c: any) => c.value) || []
    const previousIntroducedByIds = previousContacts.introduced_by?.map((c: any) => c.value) || []
    const selectedSourcedByIds = contactsByRole.sourced_by?.map((c: any) => c.value) || []
    const previousSourcedByIds = previousContacts.sourced_by?.map((c: any) => c.value) || []

    const shouldUpdatePrevious =
      (hasSelectedData && !hasPreviousData) ||
      (!hasInitializedRef.current && hasSelectedData) ||
      (hasInitializedRef.current &&
        (JSON.stringify(selectedIntroducedByIds.sort()) !== JSON.stringify(previousIntroducedByIds.sort()) ||
          JSON.stringify(selectedSourcedByIds.sort()) !== JSON.stringify(previousSourcedByIds.sort())))

    if (shouldUpdatePrevious) {
      setPreviousContacts({
        introduced_by: [...contactsByRole.introduced_by],
        sourced_by: [...contactsByRole.sourced_by]
      })
      hasInitializedRef.current = true
    }

    setSelectedContacts(contactsByRole)
  }, [
    data[AccountRecordFields.prog__other_team],
    data[AccountRecordFields.contact_accounts__introduced_by],
    data[AccountRecordFields.contact_accounts__sourced_by],
    data.introduced_by_detail,
    data.sourced_by_details
  ])

  const handlePatchSuccessLocal = async (patchRecordId: string, updates: Record<string, any>) => {
    const currentToken = getAccessToken(store.getState().auth)
    if (companyId && currentToken) {
      try {
        const response = await fetchCompanyById(companyId, currentToken)
        if (response.data && !response.error) {
          dispatch(updateCompanyRecord({ companyId, updates: response.data }))
        }
      } catch (error) {
        console.error('Error refetching company after patch:', error)
      }
    }

    if (onPatchSuccess) {
      onPatchSuccess(patchRecordId, updates)
    } else {
      dispatch(
        updateCompanyRecord({
          companyId: patchRecordId,
          updates
        })
      )
    }
  }

  const generalFields = useMemo(
    () => getGeneralFields(data, selectedContacts, previousContacts, companyLookupId),
    [data, selectedContacts, previousContacts, companyLookupId]
  )

  return (
    <FormSection
      data-debug='general-section'
      data-component='GeneralSection'
      title='Sourcing'
      titleColor='purple'
      className='shadow-sm border border-gray-200'
      {...cardProps}
    >
      <div data-debug='general-content'>
        <InputManager
          inputMode='read'
          labelPosition='inline'
          fields={generalFields}
          onChange={(key, value) => {
            if (key === AccountRecordFields.contact_accounts__introduced_by) {
              setSelectedContacts((prev) => ({
                ...prev,
                introduced_by: Array.isArray(value) ? value : []
              }))
            } else if (key === AccountRecordFields.contact_accounts__sourced_by) {
              setSelectedContacts((prev) => ({
                ...prev,
                sourced_by: Array.isArray(value) ? value : []
              }))
            }
            handleFieldChange?.(key, value)
          }}
          enableAutoPatch={!isReadOnly && !!companyId}
          patchId={companyId}
          recordId={companyId}
          onPatchSuccess={handlePatchSuccessLocal}
          className='general-section-fields'
          fieldWrapping={true}
        />
      </div>
    </FormSection>
  )
}
